import React, { useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import "./Dashboard.css";

import TotalUserIcon from "../../../icons/svg-cons/TotalUserIcon";
import ActiveUserIcon from "../../../icons/svg-cons/ActiveUserIcon";
import RequestPendingIcon from "../../../icons/svg-cons/RequestPendingIcon";
import InactiveUserIcon from "../../../icons/svg-cons/InactiveUserIcon";

import { WeeklyEngagement } from "../../../apis/dashboardApis";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import { fetchDashboard } from "../../../redux/slices/dashboardSlice";
import Loader from "../../../components/custom-loader/Loader";
import { getCurrentYear } from "../../../utils/getCurrentYear";

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, loading, fetched } = useAppSelector((state) => state.dashboard);
  const currentYear = getCurrentYear();

  useEffect(() => {
    if (!fetched) {
      dispatch(
        fetchDashboard({
          startDate: `${currentYear}-01-01`,
          endDate: `${currentYear}-12-31`,
        }),
      );
    }
  }, [dispatch, fetched]);

  const getMetricValue = (name: string): number =>
    (data.find((m) => m.name === name)?.count as number) || 0;

  const weeklyEngagement: WeeklyEngagement[] =
    (data.find((m) => m.name === "Users Engaged Per Week")
      ?.count as WeeklyEngagement[]) || [];

  const stats = useMemo(
    () => [
      {
        label: "Total Users",
        value: getMetricValue("Total Users"),
        icon: <TotalUserIcon />,
      },
      {
        label: "Active Users",
        value: getMetricValue("Active Users"),
        icon: <ActiveUserIcon />,
      },
      {
        label: "Inactive Users",
        value: getMetricValue("Inactive Users"),
        icon: <InactiveUserIcon />,
      },
      {
        label: "Request Pending",
        value: getMetricValue("Pending Users"),
        icon: <RequestPendingIcon />,
      },
    ],
    [data],
  );

  // const chartOptions = {
  //   chart: { toolbar: { show: false } },
  //   xaxis: {
  //     categories: weeklyEngagement.map((w) => `${w.week_number}`),
  //   },
  //   dataLabels: { enabled: true },
  // };

  const chartOptions = {
    chart: { id: "user-engagement", toolbar: { show: false } },
    xaxis: {
      categories: weeklyEngagement.map((w) => `${w.name}`),
      title: { text: "Weeks" },
    },
    yaxis: { title: { text: "Users" } },
    dataLabels: { enabled: true, offsetY: -10 },
    colors: ["#5CE1E6"],
    plotOptions: {
      bar: {
        borderRadius: 15,
        borderRadiusApplication: "start" as any,
        horizontal: false,
      },
    },
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
  };

  const chartSeries = [
    {
      name: "Users Engaged",
      data: weeklyEngagement.map((w) => Number(w.users || 0)),
    },
  ];

  return (
    <div className="dashboard-container">
      <h1 className="dashboard-header">Dashboard</h1>

      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="stats-cards">
            {stats.map((stat, idx) => (
              <div key={idx} className="stat-card">
                <div>
                  <div className="stat-label">{stat.label}</div>
                  <div className="stat-value">{stat.value}</div>
                </div>
                <div className="stat-icon">{stat.icon}</div>
              </div>
            ))}
          </div>

          <Chart
            options={chartOptions}
            series={chartSeries}
            type="bar"
            height={300}
          />
        </>
      )}
    </div>
  );
};

export default Dashboard;
